import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { AppLayout } from "../shared/layout";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ExternalLink,
  Clock,
  User,
  Calendar,
  MessageSquare,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import {
  getTask,
  deleteTask,
  addTaskComment,
  updateTaskStatus,
} from "../../../data/requests/task-requests";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { toast } from "@/components/ui/use-toast";
import AuthStore from "../../../stores/AuthStore";

const TaskDetail = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");

  const { data: task, isLoading } = useQuery({
    queryKey: ["task", id],
    queryFn: () => getTask(id).then((res) => res.data),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status }) => updateTaskStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(["task", id]);
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["my-task-count"]);
      toast({
        title: "Başarılı",
        description: "Task durumu güncellendi",
      });
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Task durumu güncellenirken hata oluştu",
        variant: "destructive",
      });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (content) => addTaskComment(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries(["task", id]);
      setComment("");
      toast({
        title: "Başarılı",
        description: "Yorum eklendi",
      });
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Yorum eklenirken hata oluştu",
        variant: "destructive",
      });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: () => deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["my-task-count"]);
      toast({
        title: "Başarılı",
        description: "Görev silindi",
      });
      navigate("/app/tasks");
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Task silinirken hata oluştu",
        variant: "destructive",
      });
    },
  });

  const handleStatusChange = (status) => {
    updateStatusMutation.mutate({ status });
  };

  const handleAddComment = () => {
    if (!comment.trim()) return;
    addCommentMutation.mutate(comment);
  };

  const handleDelete = () => {
    if (window.confirm("Bu görevi silmek istediğinizden emin misiniz?")) {
      deleteTaskMutation.mutate();
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      Acil: "destructive",
      Normal: "default",
      Düşük: "secondary",
    };
    return <Badge variant={variants[priority] || "default"}>{priority}</Badge>;
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6">
          <div className="text-center">Yükleniyor...</div>
        </div>
      </AppLayout>
    );
  }

  if (!task) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6">
          <div className="text-center">Görev bulunamadı</div>
        </div>
      </AppLayout>
    );
  }

  const isCreator = task.createdBy.id === AuthStore.user?.id;
  const assignedUsers = task.assignedUsers || [];

  return (
    <AppLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/app/tasks")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{task.title}</h1>
            <p className="text-muted-foreground mt-1">Görev Detayları</p>
          </div>
          {isCreator && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Ana İçerik */}
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Açıklama</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {task.description || "Açıklama bulunmuyor"}
                </p>
              </CardContent>
            </Card>

            {/* İlgili Sayfa */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" />
                  İlgili Sayfa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={task.pageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {task.pageUrl}
                </a>
              </CardContent>
            </Card>

            {/* Yorumlar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Yorumlar ({task.comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {task.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="border-l-2 border-gray-200 pl-4 py-2"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">
                        {comment.user.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(
                          new Date(comment.createdAt),
                          "dd MMM yyyy HH:mm",
                          {
                            locale: tr,
                          }
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {comment.content}
                    </p>
                  </div>
                ))}

                {task.comments.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">
                    Henüz yorum yapılmamış
                  </p>
                )}

                <Separator />

                <div className="space-y-2">
                  <Textarea
                    placeholder="Yorumunuzu yazın..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!comment.trim() || addCommentMutation.isPending}
                    className="w-full"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Yorum Ekle
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Yan Panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Durum</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={task.status} onValueChange={handleStatusChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Açık">Açık</SelectItem>
                    <SelectItem value="Devam Ediyor">Devam Ediyor</SelectItem>
                    <SelectItem value="Tamamlandı">Tamamlandı</SelectItem>
                    <SelectItem value="İptal">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Detaylar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-1">Öncelik</p>
                  {getPriorityBadge(task.priority)}
                </div>

                <Separator />

                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Oluşturan</p>
                    <p className="text-sm text-muted-foreground">
                      {task.createdBy.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {task.createdBy.email}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-2">
                      Atanan Kullanıcılar ({assignedUsers.length})
                    </p>
                    {assignedUsers.length > 0 ? (
                      <div className="space-y-2">
                        {assignedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="bg-muted p-2 rounded-md"
                          >
                            <p className="text-sm text-muted-foreground">
                              {user.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {user.email}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Atanan kullanıcı yok
                      </p>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Oluşturulma</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(task.createdAt), "dd MMMM yyyy HH:mm", {
                        locale: tr,
                      })}
                    </p>
                  </div>
                </div>

                {task.completedAt && (
                  <>
                    <Separator />
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-1">Tamamlanma</p>
                        <p className="text-sm text-muted-foreground">
                          {format(
                            new Date(task.completedAt),
                            "dd MMMM yyyy HH:mm",
                            {
                              locale: tr,
                            }
                          )}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
});

export default TaskDetail;
